// src/app/pages/dashboard/ETLDashboard.tsx
import React, { useEffect, useState, useMemo } from 'react'
import axios from 'axios'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Tooltip as PrimeTooltip } from 'primereact/tooltip'
import { Collapse } from 'react-bootstrap'
import { API_URL } from '../../modules/auth/core/_requests'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { format, addMonths, subDays, isAfter, isBefore, parseISO, startOfMonth, addDays } from 'date-fns'
import { ProgressSpinner } from 'primereact/progressspinner'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Modal } from 'react-bootstrap'

const statusColors: Record<string, string> = {
  Success: '#21ba45',
  Failure: '#db2828',
  IN_PROGRESS: '#ff9800',
  'did not run': '#bdbdbd',
}

// 1. Hardcoded ETLs not logging yet
const NOT_LOGGING_YET_ETLS = [
  "vendorati_etls",
  "raw-to-product-catalog-etl",
  "cost-and-price-etl",
  "amazon-sales-velocity-etl"
];

// 2. API Categories
const API_CATEGORIES = {
  ETL: 'ETL',
  THIRD_PARTY_API: 'API Health Check'
};

// Add constant for start date
const LOGGING_START_DATE = new Date('2025-02-05')

export const ETLDashboard: React.FC = () => {
  const [data, setData] = useState<any[]>([])
  const [days, setDays] = useState(90)
  const [selectedJob, setSelectedJob] = useState<string | null>(null)
  const [history, setHistory] = useState<any[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyDays, setHistoryDays] = useState(180)
  const [open, setOpen] = useState<Record<string, boolean>>({
    'etl-accordion-header': false,
    'api-accordion-header': false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [groupByMonth, setGroupByMonth] = useState(false)
  const [modalData, setModalData] = useState<any[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [accordionStates, setAccordionStates] = useState<Record<string, boolean>>({})
  const [tooltipData, setTooltipData] = useState<{
    status: string
    date: string
    total: number
    successCount: number
    failureCount: number
    category: string
    errorCode?: string
    errorMessage?: string
    additionalInfo?: string
  } | null>(null)
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )
  const opRef = React.useRef<any>(null)

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleOpen = (periodLabel: string) => {
    setOpen(prev => ({ ...prev, [periodLabel]: !prev[periodLabel] }))
  }

  useEffect(() => {
    setIsLoading(true)
    axios
      .get(`${API_URL}/logs/etl-dashboard?days=${days}`)
      .then((res) => {
        setData(res.data)
      })
      .finally(() => setIsLoading(false))
  }, [days])

  // Get jobs by category
  const getJobsByCategory = (category: string) => {
    return Array.from(new Set(data.filter(d => d.JobCategory === category).map(d => d.JobName)));
  };

  const etlJobs = getJobsByCategory(API_CATEGORIES.ETL);
  const apiJobs = getJobsByCategory(API_CATEGORIES.THIRD_PARTY_API);
  const etlJobCount = etlJobs.length;
  const apiJobCount = apiJobs.length;

  const getCardMaxWidth = () => {
    // Return 100% for mobile and tablet devices
    if (windowWidth <= 800) return '100%';

    if (groupByMonth) return '65%';
    if (days <= 30) return '40%';
    if (days <= 60) return '75%';
    if (days > 60 && days <= 90) return '100%';
    return '100%';
  };

  // Helper: get all periods in the selected range
  type Period = { start: Date, end: Date, label: string }
  function getPeriods(startDate: Date, endDate: Date, monthsPerPeriod: number): Period[] {
    // Ensure start date is not before logging start date
    const adjustedStartDate = isBefore(startDate, LOGGING_START_DATE) ? LOGGING_START_DATE : startDate
    const periods: Period[] = []
    let periodStart = startOfMonth(adjustedStartDate)
    while (isBefore(periodStart, endDate)) {
      let periodEnd = subDays(addMonths(periodStart, monthsPerPeriod), 1)
      if (isAfter(periodEnd, endDate)) periodEnd = endDate
      periods.push({
        start: periodStart,
        end: periodEnd,
        label: monthsPerPeriod === 1
          ? `${format(periodStart, 'MMM yy')}`
          : `${format(periodStart, 'MMM yy')} - ${format(periodEnd, 'MMM yy')}`
      })
      periodStart = addMonths(periodStart, monthsPerPeriod)
    }
    return periods
  }

  // Helper: get all days in the selected range (for aggregate and subcategories)
  function getDateRangeArray(startDate: Date, endDate: Date) {
    const arr: string[] = []
    let d = new Date(startDate)
    while (d <= endDate) {
      arr.push(format(d, 'yyyy-MM-dd'))
      d = addDays(d, 1)
    }
    return arr
  }

  // Calculate the min and max date from the data
  const allDates = data.map(d => d.JobDate).filter(Boolean)
  const maxDate = allDates.length ? parseISO(allDates[0]) : new Date()
  const minDate = allDates.length ? parseISO(allDates[allDates.length - 1]) : LOGGING_START_DATE
  // Calculate the selected range
  const today = new Date()
  const selectedEnd = days === 0 ? maxDate : today
  const selectedStart = days === 0 ? minDate : subDays(today, days - 1)

  // Get periods for main ETL bar (1 month per row)
  const mainPeriods: Period[] = getPeriods(selectedStart, selectedEnd, 1)
  // Get periods for subcategories (2 months per row)
  const subcatPeriods: Period[] = getPeriods(selectedStart, selectedEnd, 2)

  // Update the dropdown options based on available data
  const dayOptions = useMemo(() => {
    const options = [
      { label: 'Last 30 Days', value: 30 },
      { label: 'Last 60 Days', value: 60 },
      { label: 'Last 90 Days', value: 90 },
      { label: 'Last 180 Days', value: 180 },
      { label: 'Last 365 Days', value: 365 }
    ]

    // Filter out options that would go before LOGGING_START_DATE
    return options.filter(option => {
      const startDate = subDays(today, option.value - 1)
      return !isBefore(startDate, LOGGING_START_DATE)
    })
  }, [today])

  const handleHistory = (job: string) => {
    setSelectedJob(job)
    setHistoryOpen(true)
    fetchHistory(job, historyDays)
  }

  const fetchHistory = (job: string, days: number) => {
    axios.get(`${API_URL}/logs/etl-history/${encodeURIComponent(job)}?days=${days}`).then((res) => {
      const sortedHistory = res.data.sort((a: any, b: any) => 
        new Date(b.JobStartTime).getTime() - new Date(a.JobStartTime).getTime()
      )
      setHistory(sortedHistory)
    })
  }

  // Sanitize tooltip id
  const sanitizeId = (str: string) => str.replace(/[^a-zA-Z0-9_-]/g, '_')

  function getBarColor(successCount: number, failureCount: number, total: number) {
    if (total === 0) return statusColors['did not run']
    const failureRate = failureCount / total
    
    if (failureRate === 0) return statusColors['Success']
    if (failureRate > 0.75) return statusColors['Failure']
    if (failureRate > 0.5) return '#ffe066' // Light Yellow
    if (failureRate >= 0.2) return statusColors['IN_PROGRESS']
    
    // For low failure rates, show different colors based on actual failure count
    if (failureCount === 1) return '#ff9800' // Medium Orange - 1 failure
    if (failureCount === 2) return '#ff9800' // Orange - 2 failures
    if (failureCount >= 3) return '#f57c00' // Dark Orange - 3+ failures
    
    return statusColors['Success']
  }

  // Helper: get status icon and message
  function getStatusIconAndMsg(successCount: number, failureCount: number, total: number, category?: string) {
    if (total === 0) {
      return {
        icon: 'pi pi-minus-circle text-muted',
        msg: category === API_CATEGORIES.THIRD_PARTY_API ? 'No API Health Check Ran' : 'No ETL Ran'
      }
    }
    if (failureCount === 0) {
      return {
        icon: 'pi pi-check-circle text-success', 
        msg: 'All Successful'
      }
    }
    if (successCount === 0) {
      return {
        icon: 'pi pi-times-circle text-danger',
        msg: 'All Failed'
      }
    }
    
    // More descriptive messages for partial failures
    if (failureCount === 1) {
      return {
        icon: 'pi pi-exclamation-triangle text-warning',
        msg: `1 out of ${total} Failed`
      }
    }
    if (failureCount === 2) {
      return {
        icon: 'pi pi-exclamation-triangle text-warning',
        msg: `2 out of ${total} Failed`
      }
    }
    if (failureCount >= 3) {
      return {
        icon: 'pi pi-exclamation-triangle text-warning',
        msg: `${failureCount} out of ${total} Failed`
      }
    }
    
    return {
      icon: 'pi pi-exclamation-circle text-warning',
      msg: 'Partial Success'
    }
  }

  // Calculate uptime for aggregate - operational % per day based on success/failure ratio
  function getAggregateUptime(daysArr: string[], category: string) {
    let totalOperationalPercentage = 0
    let daysWithRuns = 0
    daysArr.forEach(dateStr => {
      const entries = data.filter(d => d.JobDate === dateStr && d.JobCategory === category)
      const total = entries.length
      const failureCount = entries.filter(e => e.Status === 'Failure').length
      if (total > 0) {
        daysWithRuns++
        // Calculate operational percentage for this day based on success rate
        const successRate = (total - failureCount) / total
        totalOperationalPercentage += successRate
      }
    })
    return daysWithRuns > 0 ? ((totalOperationalPercentage / daysWithRuns) * 100).toFixed(3) : '0.000'
  }

  return (
    <div className='container-fluid py-4 position-relative'>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <ProgressSpinner style={{width: 60, height: 60}} strokeWidth="4" />
        </div>
      )}
      <div className='card m-1 position-relative pt-6'>
        <div className='card-header  pb-4 mb-4 custom-header'>
          <div className='w-100 d-flex justify-content-between align-items-start'>
            <h2 className='card-title mb-0'>Logs Status</h2>
            <div style={{minWidth: 120, display: 'flex', alignItems: 'center', gap: 15, justifyContent: 'flex-end', flex: 1}}>
              <button
                className='btn btn-sm btn-primary'
                style={{ fontSize: 13, padding: '4px 12px', borderRadius: 4 }}
                onClick={() => setGroupByMonth(g => !g)}
              >
                {groupByMonth ? 'Show All Months' : 'Show Per Month Rows'}
              </button>
              <Dropdown value={days} options={dayOptions} onChange={(e) => setDays(e.value)} style={{ width: 'max-content', verticalAlign: 'middle' }} />
            </div>
          </div>
          <div className='w-100 d-flex justify-content-start align-items-start'>
            <span className='text-help underline text-underline' style={{fontSize: 12, marginLeft:2 }}>Uptime is the average operational percentage per day based on success/failure ratios.</span>
          </div>
        </div>
        <div className='card-body bg-body text-body'>
          <div className='card w-60 md-w-100 sm-w-100 ml-auto p-6 mt-5 mb-5 shadow' style={{maxWidth: getCardMaxWidth()}}>
            {/* Main ETL bar as accordion toggle */}
            <div className=''>
              <div
                className={`d-flex align-items-center etl-accordion-header-row`}
                style={{ minWidth: 'calc(100% + 8px)', marginLeft: '-4px', gap: 8, fontWeight: 700, fontSize: 17, borderRadius: 3, cursor: 'pointer', padding: '10px'}}
                onClick={() => toggleOpen('etl-accordion-header')}
                id='etl-accordion-header'
              >
                <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                  <i className='pi pi-database text-primary' style={{fontSize: 18}} />
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                    ETLs
                  </span>
                  <span style={{fontWeight: 400, fontSize: 14}}>({etlJobCount} components)</span>
                </span>
                <span style={{marginLeft: 'auto', color: '#1976d2', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap'}}>
                  Uptime: {getAggregateUptime(getDateRangeArray(selectedStart, selectedEnd), API_CATEGORIES.ETL)}%
                </span>
                <span className='d-flex align-items-center' style={{gap: 4}}>
                  <span className='text-muted' style={{fontSize: 11, marginLeft:4, lineHeight: '1 !important' }}>{open['etl-accordion-header'] ? 'Hide details' : 'Show details'}</span>
                  <i className={`pi  ${open['etl-accordion-header'] ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{marginLeft:4, fontSize: 18, transition: 'transform 0.2s'}}></i>
                  
                </span>
              </div>
            <div className='mt-3' style={{display: 'flex', flexDirection: groupByMonth ? 'column' : 'row', gap: 18, flexWrap: 'wrap'}}>
              {(groupByMonth ? mainPeriods : [{label: 'All', start: selectedStart, end: selectedEnd}]).map((period, pIdx) => {
                const daysInPeriod = getDateRangeArray(period.start, period.end)
                return (
                  <div key={period.label} style={{display: 'flex', alignItems: 'center', gap: 16}}>
                    {groupByMonth && <div style={{minWidth: 60, fontWeight: 400, color: '#888', fontSize: 13, textAlign: 'left'}}>{period.label}</div>}
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', minHeight: 16}}>
                      {daysInPeriod.map((dateStr, idx) => {
                        const entries = data.filter(d => d.JobDate === dateStr && d.JobCategory === API_CATEGORIES.ETL)
                        const total = entries.length
                        const failureCount = entries.filter(e => e.Status === 'Failure').length
                        const successCount = entries.filter(e => e.Status === 'Success').length
                        const barColor = getBarColor(successCount, failureCount, total)
                        const tooltipId = sanitizeId(`agg-bar-${period.label}-${dateStr}`)
                        const isFirst = idx === 0
                        const isLast = idx === daysInPeriod.length - 1
                        return (
                          <div
                            key={dateStr}
                            id={tooltipId}
                            style={{
                              width: 8,
                              height: 30,
                              borderTopLeftRadius: isFirst ? 3 : 0,
                              borderBottomLeftRadius: isFirst ? 3 : 0,
                              borderTopRightRadius: isLast ? 3 : 0,
                              borderBottomRightRadius: isLast ? 3 : 0,
                              background: barColor,
                              display: 'inline-block',
                              margin: 0,
                              border: `1px solid ${barColor}`,
                              boxSizing: 'border-box',
                              transition: 'border 0.2s',
                              cursor: total > 0 ? 'pointer' : 'default',
                            }}
                            onMouseEnter={e => {
                              setTooltipData({
                                status: getStatusIconAndMsg(successCount, failureCount, total, API_CATEGORIES.ETL).msg,
                                date: format(new Date(dateStr), 'MMM dd, yyyy'),
                                total,
                                successCount,
                                failureCount,
                                category: API_CATEGORIES.ETL
                              })
                              opRef.current && opRef.current.show(e)
                            }}
                            onMouseLeave={() => {
                              opRef.current && opRef.current.hide()
                            }}
                            onClick={() => {
                              if (total > 0) {
                                setModalData(entries)
                                setModalOpen(true)
                              }
                            }}
                          ></div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
            </div>
            {/* Accordion for subcategories */}
            <Collapse in={open['etl-accordion-header'] !== false}>
              <div id='etl-accordion-body' className='mt-3 card border-0  p-4 pt-4 pb-4 mt-2'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {etlJobs.map((job) => (
                    <div key={job} className='border-bottom pb-4 mb-2'>
                      <div className='w-100 mb-2 d-flex align-items-start justify-content-between'>
                        <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i
                            className='pi pi-history text-info'
                            style={{ fontSize: 14, cursor: 'pointer' }}
                            onClick={() => handleHistory(job)}
                            data-pr-tooltip='View history'
                            data-pr-position='top'
                          />
                          {job}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                          <div style={{ color: '#1976d2', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>
                            Uptime: {(() => {
                              // Calculate uptime for this job
                              let operationalDays = 0;
                              let daysWithRuns = 0;
                              const allDays = groupByMonth ? getDateRangeArray(selectedStart, selectedEnd) : getDateRangeArray(selectedStart, selectedEnd);
                              allDays.forEach(dateStr => {
                                const entries = data.filter(d => d.JobName === job && d.JobDate === dateStr && d.JobCategory === API_CATEGORIES.ETL)
                                const total = entries.length
                                const failureCount = entries.filter(e => e.Status === 'Failure').length
                                if (total > 0) {
                                  daysWithRuns++;
                                  if (failureCount === 0) operationalDays++;
                                }
                              });
                              return daysWithRuns > 0 ? ((operationalDays / daysWithRuns) * 100).toFixed(3) : '0.000';
                            })()}%
                          </div>
                          <button
                            className='btn btn-sm btn-info'
                            style={{ fontSize: 13, padding: '4px 12px', borderRadius: 4 }}
                            onClick={() => handleHistory(job)}
                          >
                            History
                          </button>
                        </div>
                      </div>
                      <div className='mt-2' style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                        {(groupByMonth ? subcatPeriods : [{label: 'All', start: selectedStart, end: selectedEnd}]).map((period) => {
                          const daysInPeriod = getDateRangeArray(period.start, period.end)
                          return (
                            <div key={period.label} style={{display: 'flex', alignItems: 'center', gap: 16}}>
                              {groupByMonth && <div style={{minWidth: 100, fontWeight: 400, color: '#888', fontSize: 13, textAlign: 'left'}}>{period.label}</div>}
                              <div style={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', minHeight: 16}}>
                                {daysInPeriod.map((dateStr, idx) => {
                                  const entries = data.filter(d => d.JobName === job && d.JobDate === dateStr && d.JobCategory === API_CATEGORIES.ETL)
                                  const total = entries.length
                                  const failureCount = entries.filter(e => e.Status === 'Failure').length
                                  const successCount = entries.filter(e => e.Status === 'Success').length
                                  const barColor = getBarColor(successCount, failureCount, total)
                                  const tooltipId = sanitizeId(`subcat-bar-${job}-${period.label}-${dateStr}`)
                                  const isFirst = idx === 0
                                  const isLast = idx === daysInPeriod.length - 1
                                  return (
                                    <div
                                      key={dateStr}
                                      id={tooltipId}
                                      style={{
                                        width: 8,
                                        height: 30,
                                        borderTopLeftRadius: isFirst ? 3 : 0,
                                        borderBottomLeftRadius: isFirst ? 3 : 0,
                                        borderTopRightRadius: isLast ? 3 : 0,
                                        borderBottomRightRadius: isLast ? 3 : 0,
                                        background: barColor,
                                        display: 'inline-block',
                                        margin: 0,
                                        border: `1px solid ${barColor}`,
                                        boxSizing: 'border-box',
                                        transition: 'border 0.2s',
                                        cursor: total > 0 ? 'pointer' : 'default',
                                      }}
                                      onMouseEnter={e => {
                                        setTooltipData({
                                          status: getStatusIconAndMsg(successCount, failureCount, total, API_CATEGORIES.ETL).msg,
                                          date: format(new Date(dateStr), 'MMM dd, yyyy'),
                                          total,
                                          successCount,
                                          failureCount,
                                          category: API_CATEGORIES.ETL
                                        })
                                        opRef.current && opRef.current.show(e)
                                      }}
                                      onMouseLeave={() => {
                                        opRef.current && opRef.current.hide()
                                      }}
                                      onClick={() => {
                                        if (total > 0) {
                                          setModalData(entries)
                                          setModalOpen(true)
                                        }
                                      }}
                                    ></div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {/* Hardcoded ETLs not logging yet */}
                  {NOT_LOGGING_YET_ETLS.map((job) => (
                    <div key={job} className='mb-3'>
                      <div className='d-flex align-items-center gap-3 p-3 bg-light border rounded shadow-sm' style={{opacity: 0.7}}>
                        <i className='pi pi-clock text-info' style={{fontSize: 18}} />
                        <span style={{fontWeight: 600}}>{job}</span>
                        <span className='badge bg-secondary ms-2'>Not logging yet – coming soon!</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Collapse>
          </div>

          {/* API Section */}
          <div className='card w-60 md-w-100 sm-w-100 ml-auto p-6 mt-5 mb-5 shadow' style={{maxWidth: getCardMaxWidth()}}>
            <div className=''>
              <div
                className={`d-flex align-items-center api-accordion-header-row`}
                style={{ minWidth: 'calc(100% + 8px)', marginLeft: '-4px', gap: 8, fontWeight: 700, fontSize: 17, borderRadius: 3, cursor: 'pointer', padding: '10px'}}
                onClick={() => toggleOpen('api-accordion-header')}
                id='api-accordion-header'
              >
                <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                  <i className='pi pi-server text-primary' style={{fontSize: 18}} />
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                    APIs
                  </span>
                  <span style={{fontWeight: 400, fontSize: 14}}>({apiJobCount} components)</span>
                </span>
                <span style={{marginLeft: 'auto', color: '#1976d2', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap'}}>
                  Uptime: {getAggregateUptime(getDateRangeArray(selectedStart, selectedEnd), API_CATEGORIES.THIRD_PARTY_API)}%
                </span>
                <span className='d-flex align-items-center' style={{gap: 4}}>
                  <span className='text-muted' style={{fontSize: 11, marginLeft:4, lineHeight: '1 !important' }}>{open['api-accordion-header'] ? 'Hide details' : 'Show details'}</span>
                  <i className={`pi ${open['api-accordion-header'] ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{marginLeft:4, fontSize: 18, transition: 'transform 0.2s'}}></i>
                </span>
              </div>
              <div className='mt-3' style={{display: 'flex', flexDirection: groupByMonth ? 'column' : 'row', gap: 18, flexWrap: 'wrap'}}>
                {(groupByMonth ? mainPeriods : [{label: 'All', start: selectedStart, end: selectedEnd}]).map((period, pIdx) => {
                  const daysInPeriod = getDateRangeArray(period.start, period.end)
                  return (
                    <div key={period.label} style={{display: 'flex', alignItems: 'center', gap: 16}}>
                      {groupByMonth && <div style={{minWidth: 60, fontWeight: 400, color: '#888', fontSize: 13, textAlign: 'left'}}>{period.label}</div>}
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', minHeight: 16}}>
                        {daysInPeriod.map((dateStr, idx) => {
                          const entries = data.filter(d => d.JobDate === dateStr && d.JobCategory === API_CATEGORIES.THIRD_PARTY_API)
                          const total = entries.length
                          const failureCount = entries.filter(e => e.Status === 'Failure').length
                          const successCount = entries.filter(e => e.Status === 'Success').length
                          const barColor = getBarColor(successCount, failureCount, total)
                          const tooltipId = sanitizeId(`agg-bar-${period.label}-${dateStr}`)
                          const isFirst = idx === 0
                          const isLast = idx === daysInPeriod.length - 1
                          return (
                            <div
                              key={dateStr}
                              id={tooltipId}
                              style={{
                                width: 8,
                                height: 30,
                                borderTopLeftRadius: isFirst ? 3 : 0,
                                borderBottomLeftRadius: isFirst ? 3 : 0,
                                borderTopRightRadius: isLast ? 3 : 0,
                                borderBottomRightRadius: isLast ? 3 : 0,
                                background: barColor,
                                display: 'inline-block',
                                margin: 0,
                                border: `1px solid ${barColor}`,
                                boxSizing: 'border-box',
                                transition: 'border 0.2s',
                                cursor: total > 0 ? 'pointer' : 'default',
                              }}
                              onMouseEnter={e => {
                                setTooltipData({
                                  status: getStatusIconAndMsg(successCount, failureCount, total, API_CATEGORIES.THIRD_PARTY_API).msg,
                                  date: format(new Date(dateStr), 'MMM dd, yyyy'),
                                  total,
                                  successCount,
                                  failureCount,
                                  category: API_CATEGORIES.THIRD_PARTY_API
                                })
                                opRef.current && opRef.current.show(e)
                              }}
                              onMouseLeave={() => {
                                opRef.current && opRef.current.hide()
                              }}
                              onClick={() => {
                                if (total > 0) {
                                  setModalData(entries)
                                  setModalOpen(true)
                                }
                              }}
                            ></div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            {/* Accordion for subcategories */}
            <Collapse in={open['api-accordion-header'] !== false}>
              <div id='api-accordion-body' className='mt-3 card border-0 p-4 pt-4 pb-4 mt-2'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {apiJobs.map((job) => (
                    <div key={job} className='border-bottom pb-4 mb-2'>
                      <div className='w-100 mb-2 d-flex align-items-start justify-content-between'>
                        <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i
                            className='pi pi-history text-info'
                            style={{ fontSize: 14, cursor: 'pointer' }}
                            onClick={() => handleHistory(job)}
                            data-pr-tooltip='View history'
                            data-pr-position='top'
                          />
                          {job}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                          <div style={{ color: '#1976d2', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>
                            Uptime: {(() => {
                              let operationalDays = 0;
                              let daysWithRuns = 0;
                              const allDays = getDateRangeArray(selectedStart, selectedEnd);
                              allDays.forEach(dateStr => {
                                const entries = data.filter(d => d.JobName === job && d.JobCategory === API_CATEGORIES.THIRD_PARTY_API && d.JobDate === dateStr)
                                const total = entries.length
                                const failureCount = entries.filter(e => e.Status === 'Failure').length
                                if (total > 0) {
                                  daysWithRuns++;
                                  if (failureCount === 0) operationalDays++;
                                }
                              });
                              return daysWithRuns > 0 ? ((operationalDays / daysWithRuns) * 100).toFixed(3) : '0.000';
                            })()}%
                          </div>
                          <button
                            className='btn btn-sm btn-info'
                            style={{ fontSize: 13, padding: '4px 12px', borderRadius: 4 }}
                            onClick={() => handleHistory(job)}
                          >
                            History
                          </button>
                        </div>
                      </div>
                      <div className='mt-2' style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                        {(groupByMonth ? subcatPeriods : [{label: 'All', start: selectedStart, end: selectedEnd}]).map((period) => {
                          const daysInPeriod = getDateRangeArray(period.start, period.end)
                          return (
                            <div key={period.label} style={{display: 'flex', alignItems: 'center', gap: 16}}>
                              {groupByMonth && <div style={{minWidth: 100, fontWeight: 400, color: '#888', fontSize: 13, textAlign: 'left'}}>{period.label}</div>}
                              <div style={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', minHeight: 16}}>
                                {daysInPeriod.map((dateStr, idx) => {
                                  const entries = data.filter(d => d.JobName === job && d.JobCategory === API_CATEGORIES.THIRD_PARTY_API && d.JobDate === dateStr)
                                  const total = entries.length
                                  const failureCount = entries.filter(e => e.Status === 'Failure').length
                                  const successCount = entries.filter(e => e.Status === 'Success').length
                                  const barColor = getBarColor(successCount, failureCount, total)
                                  const tooltipId = sanitizeId(`api-bar-${job}-${period.label}-${dateStr}`)
                                  const isFirst = idx === 0
                                  const isLast = idx === daysInPeriod.length - 1
                                  const hasError = entries.some(e => e.Status === 'Failure')
                                  const errorEntry = entries.find(e => e.Status === 'Failure')
                                  return (
                                    <div
                                      key={dateStr}
                                      id={tooltipId}
                                      style={{
                                        width: 8,
                                        height: 30,
                                        borderTopLeftRadius: isFirst ? 3 : 0,
                                        borderBottomLeftRadius: isFirst ? 3 : 0,
                                        borderTopRightRadius: isLast ? 3 : 0,
                                        borderBottomRightRadius: isLast ? 3 : 0,
                                        background: barColor,
                                        display: 'inline-block',
                                        margin: 0,
                                        border: `1px solid ${barColor}`,
                                        boxSizing: 'border-box',
                                        transition: 'border 0.2s',
                                        cursor: 'default'
                                      }}
                                      onMouseEnter={e => {
                                        const entry = entries[0] // Get the first entry since we're showing individual status
                                        const isSuccess = entry?.Status === 'Success'
                                        setTooltipData({
                                          status: getStatusIconAndMsg(successCount, failureCount, total, API_CATEGORIES.THIRD_PARTY_API).msg,
                                          date: format(new Date(dateStr), 'MMM dd, yyyy'),
                                          total,
                                          successCount,
                                          failureCount,
                                          category: API_CATEGORIES.THIRD_PARTY_API,
                                          errorCode: !isSuccess ? entry?.ErrorCode : undefined,
                                          errorMessage: !isSuccess ? entry?.ErrorMessage : undefined,
                                          additionalInfo: entry?.AdditionalInfo
                                        })
                                        opRef.current && opRef.current.show(e)
                                      }}
                                      onMouseLeave={() => {
                                        opRef.current && opRef.current.hide()
                                      }}
                                      onClick={() => {
                                        if (total > 0) {
                                          setModalData(entries)
                                          setModalOpen(true)
                                        }
                                      }}
                                    ></div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <Dialog
          visible={historyOpen}
          onHide={() => setHistoryOpen(false)}
          header={`History for ${selectedJob}`}
          style={{ width: '50vw' }}>
          <div className="mb-3">
            <label className="me-2">Time Range:</label>
            <Dropdown 
              value={historyDays} 
              options={[
                { value: 180, label: 'Last 6 months' },
                { value: 365, label: 'Last year' },
                { value: 0, label: 'All time' }
              ]} 
              onChange={(e) => {
                setHistoryDays(e.value)
                if (selectedJob) {
                  fetchHistory(selectedJob, e.value)
                }
              }}
              style={{ width: 150 }}
            />
          </div>
          <DataTable value={history} scrollable scrollHeight="400px" paginator rows={20} rowsPerPageOptions={[20, 50,history.length]} responsiveLayout="scroll" emptyMessage="No history found.">
            <Column field="JobStartTime" header="Date" sortable  />
            <Column field="ExecutionStatus" header="Status" sortable  />
            <Column field="DurationSeconds" header="Duration" body={rowData => (rowData.DurationSeconds ? `${rowData.DurationSeconds} seconds` : 'N/A')} sortable  />
            <Column field="ServerName" header="Server" body={rowData => rowData.ServerName || 'N/A'} sortable  />
            <Column header="Error" body={rowData => [rowData.ErrorCode, rowData.ErrorMessage].filter(Boolean).join(' ')}  />
            <Column field="AdditionalInfo" header="Info" style={{ minWidth: 150 }} />
          </DataTable>
        </Dialog>
        <Dialog
          visible={modalOpen}
          onHide={() => setModalOpen(false)}
          header={`${modalData[0]?.JobCategory === API_CATEGORIES.THIRD_PARTY_API ? 'API Health Check' : 'ETL'} Run Details`}
          style={{ width: '60vw' }}>
          <div style={{ maxHeight: 600, minHeight: '25vh', overflowY: 'auto' }}>
            {modalData.length === 0 ? (
              <div>No {modalData[0]?.JobCategory === API_CATEGORIES.THIRD_PARTY_API ? 'API Health Check' : 'ETL'} ran on this day.</div>
            ) : (
              <div className='table-responsive'>
                {/* Summary Stats */}
                <div className='d-flex gap-4 mb-4 p-3 bg-light rounded'>
                  <div className='d-flex align-items-center gap-2' style={{width: 'fit-content'}}>
                    <i className='pi pi-check-circle text-success' style={{fontSize: 20}} />
                    <div className='text-success' style={{fontSize: 18, fontWeight: 600}}>{modalData.filter(e => e.Status === 'Success').length}</div>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <i className='pi pi-times-circle text-danger' style={{fontSize: 20}} />
                    <div className='text-danger' style={{fontSize: 18, fontWeight: 600}}>{modalData.filter(e => e.Status === 'Failure').length}</div>
                   
                  </div>
                  <div className='d-flex align-items-center gap-2 justify-self-end'  style={{marginLeft: 'auto'}}>
                    <i className='pi pi-exclamation-circle text-warning' style={{fontSize: 20}} />
                    <div style={{fontSize: 12}}>In Progress</div>
                      <div className='text-info' style={{fontSize: 18, fontWeight: 600}}>{modalData.filter(e => e.Status === 'IN_PROGRESS').length}</div>
                  </div>
                </div>

                {/* API Groups */}
                {Array.from(new Set(modalData.map(e => e.JobName))).map(jobName => {
                  const jobEntries = modalData.filter(e => e.JobName === jobName)
                  const hasFailure = jobEntries.some(e => e.Status === 'Failure')
                  const failureEntry = jobEntries.find(e => e.Status === 'Failure')
                  
                  return (
                    <div key={jobName} className='mb-3'>
                      <div 
                        className='d-flex align-items-center justify-content-between p-3 bg-body shadow-sm rounded'
                        style={{cursor: 'pointer'}}
                        onClick={() => setAccordionStates(prev => ({ ...prev, [jobName]: !prev[jobName] }))}
                      >
                        <div className='d-flex align-items-center gap-3'>
                          <i className={`pi ${accordionStates[jobName] ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                          <span style={{fontWeight: 600}}>{jobName}</span>
                          <span className='badge bg-primary'>{jobEntries.length} runs</span>
                        </div>
                        <div className='d-flex gap-3'>
                          <span className='text-success'>
                            <i className='pi pi-check-circle' /> {jobEntries.filter(e => e.Status === 'Success').length}
                          </span>
                          <span className='text-danger'>
                            <i className='pi pi-times-circle' /> {jobEntries.filter(e => e.Status === 'Failure').length}
                          </span>
                        </div>
                      </div>
                      
                      {/* Show status and error info without expanding */}
                      <div className='mt-2 p-3 bg-light rounded'>
                        <div className='d-flex flex-column gap-2'>
                          <div className='d-flex align-items-center gap-2'>
                            <i className={`pi ${hasFailure ? 'pi-times-circle text-danger' : 'pi-check-circle text-success'}`} />
                            <span style={{fontWeight: 500}}>Status: {hasFailure ? 'Failure' : 'Success'}</span>
                          </div>
                          {hasFailure && failureEntry && (
                            <>
                              <div className='d-flex align-items-center gap-2'>
                                <i className='pi pi-exclamation-triangle text-danger' />
                                <span>Error: {failureEntry.ErrorCode}</span>
                              </div>
                              <div className='d-flex align-items-center gap-2'>
                                <i className='pi pi-info-circle text-danger' />
                                <span>Error Message: {failureEntry.ErrorMessage}</span>
                              </div>
                            </>
                          )}
                          {jobEntries[0]?.AdditionalInfo && (
                            <div className='d-flex align-items-center gap-2'>
                              <i className='pi pi-info-circle text-primary' />
                              <span>Info: {jobEntries[0].AdditionalInfo}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Collapse in={accordionStates[jobName]}>
                        <div className='mt-2'>
                          <DataTable value={jobEntries} scrollable scrollHeight="400px" paginator rows={10} rowsPerPageOptions={[10, 20, jobEntries.length]} responsiveLayout="scroll" emptyMessage="No entries found.">
                            <Column field="JobStartTime" header="Date" sortable />
                            <Column field="Status" header="Status" sortable />
                            <Column field="DurationSeconds" header="Duration" body={rowData => (rowData.DurationSeconds ? `${rowData.DurationSeconds} seconds` : 'N/A')} sortable />
                            <Column field="ServerName" header="Server" body={rowData => rowData.ServerName || 'N/A'} sortable />
                            <Column header="Error" body={rowData => [rowData.ErrorCode, rowData.ErrorMessage].filter(Boolean).join(' ')} />
                            <Column field="AdditionalInfo" header="Info" style={{ minWidth: 150 }} />
                          </DataTable>
                        </div>
                      </Collapse>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </Dialog>
      </div>
      <OverlayPanel ref={opRef} showCloseIcon={false} dismissable style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}>
        {tooltipData && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', padding: 0 }}>
            {tooltipData.category === API_CATEGORIES.THIRD_PARTY_API ? (
              <>
                <div className='text-primary' style={{ fontSize: 13, marginBottom: 1 }}>
                  <i className="pi pi-server" style={{ marginRight: 4 }} />
                  Status: <span style={{fontWeight: 600}}>{tooltipData.status}</span>
                </div>
                {typeof tooltipData.total === 'number' && tooltipData.total > 0 && (
                  <>
                    <div className='text-success' style={{ fontSize: 13, marginBottom: 1 }}>
                      <i className="pi pi-check-circle" style={{ marginRight: 4 }} />
                      Success: <span style={{fontWeight: 600}}>{tooltipData.successCount}</span>
                    </div>
                    <div className='text-danger' style={{ fontSize: 13, marginBottom: 1 }}>
                      <i className="pi pi-times-circle" style={{ marginRight: 4 }} />
                      Failed: <span style={{fontWeight: 600}}>{tooltipData.failureCount}</span>
                    </div>
                  </>
                )}
                {!tooltipData.status.includes('Success') && tooltipData.errorCode && (
                  <div className='text-danger' style={{ fontSize: 13, marginBottom: 1 }}>
                    <i className="pi pi-times-circle" style={{ marginRight: 4 }} />
                    Error: <span style={{fontWeight: 600}}>{tooltipData.errorCode}</span>
                  </div>
                )}
                {!tooltipData.status.includes('Success') && tooltipData.errorMessage && (
                  <div className='text-danger' style={{ fontSize: 13, marginBottom: 1 }}>
                    Error Message: {tooltipData.errorMessage}
                  </div>
                )}
                {tooltipData.additionalInfo && (
                  <div className='text-primary' style={{ fontSize: 13, marginBottom: 1 }}>
                    <i className="pi pi-info-circle" style={{ marginRight: 4 }} />
                    Info: {tooltipData.additionalInfo}
                  </div>
                )}
              </>
            ) : (
              <>
                {typeof tooltipData.total === 'number' && tooltipData.total > 0 ? (
                  <>
                    <div className='text-primary' style={{ fontSize: 13, marginBottom: 1 }}>
                      <i className="pi pi-database" style={{ marginRight: 4 }} />
                      ETLs Ran: <span style={{fontWeight: 600}}>{tooltipData.total}</span> times
                    </div>
                    <div className='text-success' style={{ fontSize: 13, marginBottom: 1 }}>
                      <i className="pi pi-check-circle" style={{ marginRight: 4 }} />
                      Success: <span style={{fontWeight: 600}}>{tooltipData.successCount}</span>
                    </div>
                    <div className='text-danger' style={{ fontSize: 13, marginBottom: 1 }}>
                      <i className="pi pi-times-circle" style={{ marginRight: 4 }} />
                      Failed: <span style={{ fontWeight: 600}}>{tooltipData.failureCount}</span>
                    </div>
                  </>
                ) : (
                  <div className='text-warning' style={{ fontSize: 13, marginBottom: 1 }}>
                    <i className="pi pi-minus-circle" style={{ marginRight: 4 }} />
                    {tooltipData.category === API_CATEGORIES.THIRD_PARTY_API ? 'No API Health Check Ran' : 'No ETL Ran'}
                  </div>
                )}
              </>
            )}
            <div style={{ borderTop: '1px solid #bdbdbd', width: '100%', marginTop: 3, paddingTop: 3, textAlign: 'center', fontSize: 12 }}>
              {((tooltipData.category === API_CATEGORIES.ETL) || 
                (tooltipData.category === API_CATEGORIES.THIRD_PARTY_API && tooltipData.total > 0)) && 
                typeof tooltipData.total === 'number' && tooltipData.total > 0 && (
                <div style={{ fontWeight: 600}}>Click Bar to View Details</div>
              )}
              <div className='text-primary' style={{ fontSize: 13, marginBottom: 2 }}>{tooltipData.date}</div>
            </div>
          </div>
        )}
      </OverlayPanel>
      <PrimeTooltip target='.pi-history' content='View history' />
    </div>
  )
}